/**
 * HR/Owner — ดูประวัติการเข้างานของพนักงานรายคนทั้งช่วง
 * (มา/ขาด/ลา + เข้า-ออกกี่โมงรายวัน + สาย). เลือกพนักงาน + ช่วงเวลา.
 * ใช้ AttendanceTimeline ร่วมกับหน้า "ของฉัน".
 * + ลงเวลาย้อนหลังให้พนักงานที่ลืมสแกน (เขียนทับรอบนั้นของวันนั้น มี audit + แจ้งพนักงาน)
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, History } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useEmployees, useEmployeeAttendance, useManualAttendanceEntry } from '../../hooks';
import { EmployeePicker } from '../../components/ui/EmployeePicker';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import {
  AttendanceTimeline,
  AttendanceRangePresets,
  rangeForPreset,
  thisMonthRange,
  type RangePreset,
} from '../../components/domain/AttendanceTimeline';

interface EmpOpt {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
  status?: string | null;
  department?: string | null;
  position?: string | null;
}

const KIND_OPTIONS = [
  { value: 'morning_in', label: 'เข้างาน' },
  { value: 'evening_out', label: 'เลิกงาน' },
  { value: 'lunch_out', label: 'พักเที่ยง' },
  { value: 'lunch_in', label: 'กลับจากพัก' },
] as const;
type ManualKind = (typeof KIND_OPTIONS)[number]['value'];

function todayThai(): string {
  return new Date(Date.now() + 7 * 3_600_000).toISOString().slice(0, 10);
}

function ManualEntryCard({ empCode, empName }: { empCode: string; empName: string }) {
  const toast = useToast();
  const consequence = useConsequence();
  const save = useManualAttendanceEntry();

  const maxDate = todayThai();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(maxDate);
  const [kind, setKind] = useState<ManualKind>('morning_in');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');

  async function submit() {
    if (!date) return toast.error('เลือกวันที่');
    if (!time) return toast.error('ระบุเวลา');
    if (!reason.trim()) return toast.error('ต้องระบุเหตุผล — จะถูกบันทึกในประวัติการแก้ไขและแจ้งพนักงาน');
    const kindLabel = KIND_OPTIONS.find((k) => k.value === kind)?.label ?? kind;
    const ok = await consequence({
      title: 'ลงเวลาย้อนหลัง',
      intro: 'เมื่อกดยืนยัน ระบบจะบันทึกเวลานี้แทนการสแกนจริง',
      lines: [
        { label: 'พนักงาน', value: empName },
        { label: 'วันที่', value: date },
        { label: `เวลา${kindLabel}`, value: `${time} น.`, emphasis: true },
      ],
      effects: [
        'เวลานี้ถูกใช้คำนวณเงินเดือน/สาย เหมือนการสแกนจริง (ระบบติดป้ายว่าเป็นเวลาที่ลงย้อนหลัง)',
        'พนักงานได้รับแจ้งทาง LINE ว่า HR ลงเวลาให้ พร้อมเหตุผล',
        'บันทึกในประวัติการใช้งานระบบว่าใครลง ลงเมื่อไร แก้จากค่าไหน',
        'ต้องรัน "สรุปเวลาเข้างาน" ของงวดนั้นใหม่ ตัวเลขจึงจะอัปเดต',
      ],
      confirmText: 'ยืนยันลงเวลา',
    });
    if (!ok) return;
    save.mutate(
      { emp_code: empCode, date, kind, time, reason: reason.trim() },
      {
        onSuccess: (r) => {
          toast.success(r.replaced_existing ? `แก้เวลา${kindLabel}ของวันนั้นแล้ว` : `ลงเวลา${kindLabel}แล้ว`);
          setTime(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ลงเวลาไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="card p-3 mb-3">
      <button type="button" className="w-full flex items-center justify-between" onClick={() => setOpen((v) => !v)}>
        <span className="font-medium text-ink flex items-center gap-1.5 text-sm">
          <History className="w-4 h-4 text-teal" /> ลงเวลาย้อนหลัง (พนักงานลืมสแกน)
        </span>
        <span className="text-xs text-gray-400">{open ? 'ซ่อน' : 'เปิดฟอร์ม'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            ใช้กรณีพนักงานลืมสแกนและไม่ได้ยื่นคำขอเอง — ถ้าพนักงานยื่นคำขอผ่านเมนู
            "รับรองเวลาเข้างาน" อยู่แล้ว ให้ไปอนุมัติที่หน้าอนุมัติแทน จะได้ไม่ซ้ำกัน
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">วันที่</label>
              <input type="date" value={date} max={maxDate} onChange={(e) => setDate(e.target.value)} className="input w-full" />
            </div>
            <div>
              <label className="label">รอบ</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as ManualKind)} className="input w-full">
                {KIND_OPTIONS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">เวลา</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="input w-full" />
          </div>
          <div>
            <label className="label">เหตุผล (พนักงานเห็นข้อความนี้)</label>
            <input
              type="text"
              value={reason}
              maxLength={300}
              placeholder="เช่น ลืมสแกนเข้า มีลายเซ็นในใบลงเวลาสำรอง"
              onChange={(e) => setReason(e.target.value)}
              className="input w-full"
            />
          </div>
          <button type="button" className="btn-primary w-full" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'กำลังบันทึก…' : 'บันทึกเวลา'}
          </button>
        </div>
      )}
    </div>
  );
}

export function EmployeeAttendance() {
  const { data: employees } = useEmployees() as { data: EmpOpt[] | undefined };
  // ลิงก์ทะลุจากหน้า "สรุปมาสาย" (/hr/late-report) — ?emp=&from=&to= เลือก
  // พนักงานคนนั้น+ช่วงวันที่เดียวกันให้ทันที
  const [searchParams] = useSearchParams();
  const qEmp = searchParams.get('emp');
  const qFrom = searchParams.get('from');
  const qTo = searchParams.get('to');
  const [empCode, setEmpCode] = useState<string>(qEmp ?? '');
  const [range, setRange] = useState(qFrom && qTo ? { from: qFrom, to: qTo } : thisMonthRange());
  const [preset, setPreset] = useState<RangePreset>('this-month');
  const { data, isLoading } = useEmployeeAttendance(empCode || null, range);

  function applyPreset(p: RangePreset) {
    setPreset(p);
    setRange(rangeForPreset(p));
  }

  const opts = (employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation');
  const selected = opts.find((e) => e.emp_code === empCode);
  const selectedName = selected
    ? `${selected.first_name ?? ''} ${selected.last_name ?? ''}`.trim() || empCode
    : empCode;

  return (
    <AdminLayout title="ประวัติเข้างานรายคน">
      <div className="card p-3 mb-3">
        <label className="label">เลือกพนักงาน</label>
        <EmployeePicker employees={opts} value={empCode || null} onChange={(c) => setEmpCode(c ?? '')} />
      </div>

      {!empCode ? (
        <div className="card p-6 text-center text-gray-400">
          <User className="w-10 h-10 mx-auto mb-2 text-muted" />
          <p className="text-sm">เลือกพนักงานเพื่อดูประวัติการเข้างาน</p>
        </div>
      ) : (
        <>
          <ManualEntryCard empCode={empCode} empName={selectedName} />
          <AttendanceRangePresets preset={preset} onChange={applyPreset} />
          <AttendanceTimeline data={data} isLoading={isLoading} />
        </>
      )}
    </AdminLayout>
  );
}
